import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbFilterTextInput } from "abreact/input/AbFilterTextInput";
import { AbList } from "abreact/input/AbList";
import { AbHBox } from "abreact/layout/AbHBox";
import { AbHSeparator } from "abreact/layout/AbHSeparator";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { AbEllipsisAnimation } from "abreact/progress/AbEllipsisAnimation";
import { AbFilterUtils } from "abreact/utils/AbFilterUtils";
import * as AbHooks from "abreact/utils/AbHooks";
import { useCurrentValue, useOnValueChange } from "abreact/utils/AbHooks";
import { AbSelectionPropsUtils } from "abreact/utils/AbSelectionPropsUtils";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AB_ITEM_SEPARATOR, AbItemSeparator } from "abweb/collection/AbItemSeparator";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import * as React from "react";
var isNotSeparator = AbItemSeparator.isNotSeparator;
import { AbMenu } from "abreact/menu/AbMenu";

/**
 * The properties for an AbFilteredList.
 * Includes all of AbListProps, and applies these properties to the AbList subcomponent.
 * filterFunction and/or onFilterChange must be provided to implement custom filtering support.
 */

export const AbFilteredList = props => {
  const [filterString, setFilterString] = React.useState(props.initialFilterString ?? "");
  const externalFilterCounter = React.useRef(0);
  const [externalFilterHappening, setExternalFilterHappening] = React.useState(false);
  const partialRef = React.useRef({}).current;
  const isRefReady = partial => !!partial.filterBoxRef;
  const filterBoxRef = AbHooks.useComposeRef(props, partialRef, "filterBoxRef", isRefReady);
  const listRef = AbHooks.useComposeRef(props, partialRef, "listRef", isRefReady);
  const menuRef = AbHooks.useComposeRef(props, partialRef, "menuRef", isRefReady);
  const {
    filterFunction,
    externalFilter,
    filterBoxProps,
    listProps,
    id,
    style,
    dataTestId,
    autoFocus,
    tooltip,
    className,
    abRef,
    onFilteredItemsChange,
    menuItems,
    ...restOfListProps
  } = props;
  const hasMenu = !!menuItems;
  const currentOnFilteredItemsChange = useCurrentValue(onFilteredItemsChange);
  const items = React.useMemo(() => AbListHelper.from(props.items), [props.items]);
  const calculatedFilterFunction = React.useMemo(() => {
    return props.filterFunction ?? (props.externalFilter ? undefined : AbFilterUtils.FILTER_FUNCTIONS.SUBSTRING);
  }, [props.filterFunction, props.externalFilter]);
  const filteredItems = React.useMemo(() => {
    const itemsWithoutSeparators = items.filterType(isNotSeparator);
    if (filterString && calculatedFilterFunction) {
      return AbListHelper.from(itemsWithoutSeparators.filter(v => {
        return calculatedFilterFunction(filterString, AbEnriched.getItem(v), AbEnriched.getEnrichment(v)) ?? true;
      }).toArray());
    }
    return AbListHelper.from(itemsWithoutSeparators.toArray());
  }, [filterString, calculatedFilterFunction, items]);
  useOnValueChange(() => {
    currentOnFilteredItemsChange.current?.(filteredItems);
  }, filteredItems);
  const itemRendererToPass = props.itemRenderer ? (item, enrichment) => {
    return props.itemRenderer?.(item, enrichment, {
      filterString: filterString,
      isMatch: calculatedFilterFunction?.(filterString, item, enrichment) ?? false
    });
  } : undefined;
  const uniqueId = AbHooks.useUniqueId();
  const filterBoxKeyHandler = KeyboardUtilsReact.makeKeyHandler([[KeyValue.ARROW_DOWN, e => {
    e.preventDefault();
    partialRef.listRef?.focusFirstFocusable();
  }], [KeyValue.ENTER, () => {
    // Enter selects the first selectable item, if one exists (or if multiselect and the item is already selected, de-selects)
    // only enabled if there's something typed in the filter box
    if (filterString.length > 0 && filteredItems.size === 1) {
      const maybeEnrichedItem = filteredItems.itemAt(0);
      if (maybeEnrichedItem === AB_ITEM_SEPARATOR) return;
      const {
        item,
        enrichment
      } = AbEnriched.extract(maybeEnrichedItem);
      if (AbEnriched.isSelectable(enrichment)) {
        AbSelectionPropsUtils.selectionPropsSelect(props, item, maybeEnrichedItem);
      }
    }
  }]]);

  // wrap key handler for tabbing because key handler prevents default
  const filterBoxOnKeyDown = e => {
    props.filterBoxProps?.__htmlProps?.onKeyDown?.(e);
    filterBoxKeyHandler(e);
  };
  const renderFilterBox = () => {
    const {
      onChange: filterBoxOnChange,
      textInputDebounce,
      __htmlProps: __filterBoxHtmlProps,
      abRef: filterBoxPropsAbRef,
      placeholder: filterBoxPlaceholder,
      ...rest
    } = filterBoxProps ?? {};
    return /*#__PURE__*/React.createElement(AbHBox, {
      spacing: true,
      className: "ab-filtered-picker-text-input-container"
    }, /*#__PURE__*/React.createElement(AbFilterTextInput, {
      textInputDebounce: textInputDebounce ?? "default",
      value: filterString,
      onChange: text => {
        filterBoxOnChange?.(text);
        setFilterString(text);
        const maybePromise = props.externalFilter?.(text);
        if (maybePromise) {
          setExternalFilterHappening(true);
          const cachedCounter = ++externalFilterCounter.current;
          maybePromise.then(() => {
            if (cachedCounter !== externalFilterCounter.current) return;
            setExternalFilterHappening(false);
          });
        } else {
          setExternalFilterHappening(false);
        }
      },
      textInputProps: {
        ...rest,
        abRef: ReactUtils.mergeRefs(filterBoxRef, filterBoxPropsAbRef),
        __htmlProps: {
          ...__filterBoxHtmlProps,
          autoComplete: "off",
          onKeyDown: filterBoxOnKeyDown
        },
        disabled: props.disabled,
        tabIndex: props.tabIndex,
        placeholder: filterBoxPlaceholder ?? AbReactResources.filterTextInput_emptyText(),
        autoFocus: autoFocus
      }
    }));
  }; // could conditionally render an AbShadowTextInput once added

  const renderResults = () => {
    if (externalFilterHappening) return /*#__PURE__*/React.createElement(AbEllipsisAnimation, null);
    if (filteredItems.size === 0) return /*#__PURE__*/React.createElement("div", {
      className: "ab-italic ab-filter-no-results"
    }, items.size === 0 && !externalFilter ? AbReactResources.picker_noData() : AbReactResources.filter_noResults());
    return /*#__PURE__*/React.createElement(AbList, _extends({}, restOfListProps, listProps, {
      itemEventHandlers: {
        ...restOfListProps.itemEventHandlers,
        onKeyDown: (item, event) => {
          restOfListProps.itemEventHandlers?.onKeyDown?.(item, event);
          if (partialRef.listRef?.isFirstFocused() && event.key === KeyValue.ARROW_UP) {
            event.stopPropagation();
            event.preventDefault();
            FocusUtils.setFocus(partialRef.filterBoxRef?.inputElement);
          } else if (partialRef.menuRef && partialRef.listRef?.isLastFocused() && event.key === KeyValue.ARROW_DOWN) {
            event.stopPropagation();
            event.preventDefault();
            partialRef.menuRef.focusFirstItem();
          }
        }
      },
      items: filterString === "" ? items : filteredItems,
      itemRenderer: itemRendererToPass,
      abRef: listRef
    }));
  };
  return /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: tooltip
  }, /*#__PURE__*/React.createElement("div", _extends({}, ReactUtils.componentProps("ab-filtered-list", props, ["ab-filtered-picker", hasMenu && "ab-filtered-list-has-menu", props.disabled && "ab-disabled"]), {
    style: style,
    id: id ?? uniqueId
  }), renderFilterBox(), /*#__PURE__*/React.createElement(AbHSeparator, null), /*#__PURE__*/React.createElement("div", {
    className: "ab-filtered-picker-content"
  }, renderResults()), hasMenu && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AbHSeparator, null), /*#__PURE__*/React.createElement("div", {
    className: "ab-filtered-list-menu-separator"
  }), /*#__PURE__*/React.createElement(AbHSeparator, null), /*#__PURE__*/React.createElement(AbMenu, {
    items: menuItems,
    abRef: menuRef,
    eventHandlers: {
      onKeyDown: e => {
        if (!partialRef.menuRef) return;
        if (partialRef.menuRef.isFirstItemFocused() && e.key === KeyValue.ARROW_UP) {
          e.preventDefault();
          if (partialRef.listRef) partialRef.listRef?.focusLastFocusable();else partialRef.filterBoxRef?.inputElement.focus();
        } else if (partialRef.menuRef.isLastItemFocused() && e.key === KeyValue.ARROW_DOWN) {
          e.preventDefault();
          partialRef.filterBoxRef?.inputElement.focus();
        }
      }
    }
  }))));
};
//# sourceMappingURL=AbFilteredList.js.map
